import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useActiveRole } from '../../../hooks/useActiveRole';
import { MANAGE_PREFIX, MANAGE_ROLE_POLICY } from '../constants';

interface ManageRouteGuardProps {
  children: React.ReactNode;
}

/**
 * Route guard that prevents users from manually navigating to manage
 * levels above their role by checking the URL depth. If the URL is
 * too shallow for the user's role, they are redirected to `/app/manage`
 * which triggers their role-specific wrapper/redirect.
 */
export const ManageRouteGuard: React.FC<ManageRouteGuardProps> = ({ children }) => {
  const { currentRole } = useActiveRole();
  const location = useLocation();

  if (!currentRole) {
    return <Navigate to="/app/manage" replace />;
  }

  const policy = MANAGE_ROLE_POLICY[currentRole];

  // SYSTEM_ADMIN or unmapped role -> no restriction
  if (!policy) {
    return <>{children}</>;
  }

  // Count segments after "/app/manage/"
  const afterManage = location.pathname.startsWith(MANAGE_PREFIX)
    ? location.pathname.slice(MANAGE_PREFIX.length)
    : '';

  const segmentCount = afterManage.split('/').filter(Boolean).length;

  if (segmentCount < policy.minSegments) {
    // User is accessing a level above their role -> redirect to role wrapper
    return <Navigate to="/app/manage" replace />;
  }

  return <>{children}</>;
};
